import { useEffect, useMemo, useState } from "react";
import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { z } from "zod";
import {
  AlertTriangle,
  Check,
  CheckCircle2,
  Copy,
  Loader2,
  Lock,
  Mail,
  UserPlus,
} from "lucide-react";
import axios from "axios";
import { toast } from "sonner";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { addMember, inviteMember } from "@/api/projects";
import {
  useGrantablePermissions,
  type GrantablePermissions,
} from "@/hooks/useGrantablePermissions";
import type {
  AddMemberResponse,
  InviteMemberResponse,
  Role,
} from "@/api/types";

const memberFormSchema = z.object({
  email: z.string().email("Enter a valid email"),
  full_name: z
    .string()
    .min(1, "Enter their name")
    .max(255, "Too long"),
  role_id: z.string().min(1, "Pick a role"),
  // Empty = no limit (the current team); a number for a salesperson on trial.
  call_limit: z
    .string()
    .refine(
      (v) => v.trim() === "" || (/^\d+$/.test(v.trim()) && Number(v) >= 1 && Number(v) <= 100000),
      "A whole number between 1 and 100,000",
    ),
});
type MemberFormValues = z.infer<typeof memberFormSchema>;

interface InviteMemberDialogProps {
  projectSlug: string;
  open: boolean;
  onOpenChange: (open: boolean) => void;
  /** Project roles, pre-sorted (system first). */
  roles: Role[];
  onMemberAdded: () => void;
}

type Tab = "invite" | "add";

export function InviteMemberDialog({
  projectSlug,
  open,
  onOpenChange,
  roles,
  onMemberAdded,
}: InviteMemberDialogProps) {
  const [tab, setTab] = useState<Tab>("invite");
  const [inviteSuccess, setInviteSuccess] =
    useState<InviteMemberResponse | null>(null);
  const [addSuccess, setAddSuccess] = useState<AddMemberResponse | null>(null);

  useEffect(() => {
    if (!open) {
      setTab("invite");
      setInviteSuccess(null);
      setAddSuccess(null);
    }
  }, [open]);

  const grantable = useGrantablePermissions();

  // Default-role fallback: a non-superadmin admin shouldn't land on a role
  // they can't assign. We try the project default → the system default →
  // the first grantable role in the list.
  const defaultRoleId = useMemo<string>(() => {
    const grantableRoles = roles.filter((r) =>
      grantable.canGrantRole(r.permissions),
    );
    const def =
      grantableRoles.find((r) => r.is_default) ??
      grantableRoles.find((r) => r.is_system) ??
      grantableRoles[0];
    return def ? String(def.id) : "";
  }, [roles, grantable]);

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-[520px]">
        <DialogHeader>
          <DialogTitle>Add a member</DialogTitle>
          <DialogDescription>
            Invite by email so they create their own password, or add them
            manually with a generated one.
          </DialogDescription>
        </DialogHeader>

        {inviteSuccess ? (
          <InviteSuccessCard
            response={inviteSuccess}
            onClose={() => onOpenChange(false)}
          />
        ) : addSuccess ? (
          <AddSuccessCard
            response={addSuccess}
            onClose={() => onOpenChange(false)}
          />
        ) : (
          <Tabs value={tab} onValueChange={(v) => setTab(v as Tab)}>
            <TabsList className="grid w-full grid-cols-2">
              <TabsTrigger value="invite">
                <Mail className="mr-2 h-3.5 w-3.5" />
                Invite by email
              </TabsTrigger>
              <TabsTrigger value="add">
                <UserPlus className="mr-2 h-3.5 w-3.5" />
                Add manually
              </TabsTrigger>
            </TabsList>

            <TabsContent value="invite" className="mt-5">
              <InviteForm
                projectSlug={projectSlug}
                roles={roles}
                defaultRoleId={defaultRoleId}
                grantable={grantable}
                onSuccess={(r) => {
                  onMemberAdded();
                  if (r.status === "existing_user_added") {
                    onOpenChange(false);
                  } else {
                    setInviteSuccess(r);
                  }
                }}
                onCancel={() => onOpenChange(false)}
              />
            </TabsContent>

            <TabsContent value="add" className="mt-5">
              <AddForm
                projectSlug={projectSlug}
                roles={roles}
                defaultRoleId={defaultRoleId}
                grantable={grantable}
                onSuccess={(r) => {
                  onMemberAdded();
                  if (!r.is_new_user) {
                    onOpenChange(false);
                  } else {
                    setAddSuccess(r);
                  }
                }}
                onCancel={() => onOpenChange(false)}
              />
            </TabsContent>
          </Tabs>
        )}
      </DialogContent>
    </Dialog>
  );
}

// ─── Invite tab form ────────────────────────────────────────────────────────

interface FormProps<R> {
  projectSlug: string;
  roles: Role[];
  defaultRoleId: string;
  grantable: GrantablePermissions;
  onSuccess: (response: R) => void;
  onCancel: () => void;
}

function InviteForm({
  projectSlug,
  roles,
  defaultRoleId,
  grantable,
  onSuccess,
  onCancel,
}: FormProps<InviteMemberResponse>) {
  const [submitting, setSubmitting] = useState(false);

  const {
    register,
    handleSubmit,
    watch,
    setValue,
    formState: { errors },
  } = useForm<MemberFormValues>({
    resolver: zodResolver(memberFormSchema),
    defaultValues: { email: "", full_name: "", role_id: defaultRoleId, call_limit: "" },
  });

  useEffect(() => {
    if (defaultRoleId) {
      setValue("role_id", defaultRoleId, { shouldDirty: false });
    }
  }, [defaultRoleId, setValue]);

  const roleId = watch("role_id");

  async function onSubmit(values: MemberFormValues) {
    setSubmitting(true);
    try {
      const res = await inviteMember(projectSlug, {
        email: values.email,
        full_name: values.full_name,
        role_id: Number(values.role_id),
        call_limit: values.call_limit.trim() ? Number(values.call_limit) : null,
      });
      if (res.status === "existing_user_added") {
        const firstName = values.full_name.split(" ")[0] || values.email;
        toast.success(`${firstName} added to the project`);
      }
      onSuccess(res);
    } catch (err) {
      toast.error(extractInviteErrorMessage(err));
    } finally {
      setSubmitting(false);
    }
  }

  return (
    <form onSubmit={handleSubmit(onSubmit)} className="space-y-5" noValidate>
      <Fields
        errors={errors}
        emailRegister={register("email")}
        nameRegister={register("full_name")}
        limitRegister={register("call_limit")}
        roleId={roleId}
        roles={roles}
        grantable={grantable}
        setRoleId={(v) => setValue("role_id", v, { shouldDirty: true })}
      />

      <DialogFooter>
        <Button
          type="button"
          variant="outline"
          onClick={onCancel}
          disabled={submitting}
        >
          Cancel
        </Button>
        <Button
          type="submit"
          disabled={submitting}
        variant="primary"
        >
          {submitting && <Loader2 className="mr-2 h-3.5 w-3.5 animate-spin" />}
          Send invitation
        </Button>
      </DialogFooter>
    </form>
  );
}

// ─── Add tab form ──────────────────────────────────────────────────────────

function AddForm({
  projectSlug,
  roles,
  defaultRoleId,
  grantable,
  onSuccess,
  onCancel,
}: FormProps<AddMemberResponse>) {
  const [submitting, setSubmitting] = useState(false);

  const {
    register,
    handleSubmit,
    watch,
    setValue,
    formState: { errors },
  } = useForm<MemberFormValues>({
    resolver: zodResolver(memberFormSchema),
    defaultValues: { email: "", full_name: "", role_id: defaultRoleId, call_limit: "" },
  });

  useEffect(() => {
    if (defaultRoleId) {
      setValue("role_id", defaultRoleId, { shouldDirty: false });
    }
  }, [defaultRoleId, setValue]);

  const roleId = watch("role_id");

  async function onSubmit(values: MemberFormValues) {
    setSubmitting(true);
    try {
      const res = await addMember(projectSlug, {
        email: values.email,
        full_name: values.full_name,
        role_id: Number(values.role_id),
        call_limit: values.call_limit.trim() ? Number(values.call_limit) : null,
      });
      if (!res.is_new_user) {
        const firstName = res.full_name.split(" ")[0] || res.email;
        toast.success(`${firstName} added to the project`);
      }
      onSuccess(res);
    } catch (err) {
      toast.error(extractInviteErrorMessage(err));
    } finally {
      setSubmitting(false);
    }
  }

  return (
    <form onSubmit={handleSubmit(onSubmit)} className="space-y-5" noValidate>
      <Fields
        errors={errors}
        emailRegister={register("email")}
        nameRegister={register("full_name")}
        limitRegister={register("call_limit")}
        roleId={roleId}
        roles={roles}
        grantable={grantable}
        setRoleId={(v) => setValue("role_id", v, { shouldDirty: true })}
      />

      <DialogFooter>
        <Button
          type="button"
          variant="outline"
          onClick={onCancel}
          disabled={submitting}
        >
          Cancel
        </Button>
        <Button
          type="submit"
          disabled={submitting}
        variant="primary"
        >
          {submitting && <Loader2 className="mr-2 h-3.5 w-3.5 animate-spin" />}
          Create account
        </Button>
      </DialogFooter>
    </form>
  );
}

// ─── Shared fields ─────────────────────────────────────────────────────────

interface FieldsProps {
  errors: {
    email?: { message?: string };
    full_name?: { message?: string };
    role_id?: { message?: string };
    call_limit?: { message?: string };
  };
  emailRegister: ReturnType<
    ReturnType<typeof useForm<MemberFormValues>>["register"]
  >;
  nameRegister: ReturnType<
    ReturnType<typeof useForm<MemberFormValues>>["register"]
  >;
  limitRegister: ReturnType<
    ReturnType<typeof useForm<MemberFormValues>>["register"]
  >;
  roleId: string;
  roles: Role[];
  grantable: GrantablePermissions;
  setRoleId: (v: string) => void;
}

function Fields({
  errors,
  emailRegister,
  nameRegister,
  limitRegister,
  roleId,
  roles,
  grantable,
  setRoleId,
}: FieldsProps) {
  const selected = roles.find((r) => String(r.id) === roleId);
  const selectedEscalated = selected
    ? !grantable.canGrantRole(selected.permissions)
    : false;
  return (
    <>
      <div className="grid grid-cols-2 gap-3">
        <div className="space-y-1.5">
          <label className="text-[11px] font-medium uppercase tracking-wider text-slate-500">
            Email
          </label>
          <Input
            type="email"
            placeholder="name@company.com"
            autoComplete="email"
            autoFocus
            {...emailRegister}
            className="h-10 rounded-lg border-slate-200"
          />
          {errors.email?.message && (
            <p className="text-xs text-rose-600">{errors.email.message}</p>
          )}
        </div>
        <div className="space-y-1.5">
          <label className="text-[11px] font-medium uppercase tracking-wider text-slate-500">
            Full name
          </label>
          <Input
            type="text"
            placeholder="Christopher Ndour"
            autoComplete="name"
            {...nameRegister}
            className="h-10 rounded-lg border-slate-200"
          />
          {errors.full_name?.message && (
            <p className="text-xs text-rose-600">
              {errors.full_name.message}
            </p>
          )}
        </div>
      </div>

      <div className="space-y-1.5">
        <label className="text-[11px] font-medium uppercase tracking-wider text-slate-500">
          Role
        </label>
        <Select value={roleId} onValueChange={setRoleId}>
          <SelectTrigger className="h-10 rounded-lg border-slate-200">
            <SelectValue placeholder="Pick a role" />
          </SelectTrigger>
          <SelectContent>
            {roles.map((r) => (
              <RoleOption key={r.id} role={r} grantable={grantable} />
            ))}
          </SelectContent>
        </Select>
        {selectedEscalated && (
          <p className="flex items-start gap-1.5 text-xs text-amber-700">
            <AlertTriangle className="mt-0.5 h-3 w-3 shrink-0" />
            This role grants permissions you don&rsquo;t have. Only a
            superadmin can assign it.
          </p>
        )}
        {selected?.description && !selectedEscalated && (
          <p className="text-xs text-slate-500">{selected.description}</p>
        )}
        {errors.role_id?.message && (
          <p className="text-xs text-rose-600">{errors.role_id.message}</p>
        )}
      </div>

      <div className="space-y-1.5">
        <label className="text-[11px] font-medium uppercase tracking-wider text-slate-500">
          Call limit <span className="normal-case tracking-normal text-slate-400">(optional)</span>
        </label>
        <Input
          type="number"
          min={1}
          placeholder="No limit"
          {...limitRegister}
          className="h-10 w-40 rounded-lg border-slate-200"
        />
        <p className="text-xs text-slate-500">
          For a salesperson on trial, e.g. 200: the Call Center stops handing them new
          leads at the limit. Leave empty for no limit.
        </p>
        {errors.call_limit?.message && (
          <p className="text-xs text-rose-600">{errors.call_limit.message}</p>
        )}
      </div>
    </>
  );
}

// ─── Role option (with escalation guard) ───────────────────────────────────

interface RoleOptionProps {
  role: Role;
  grantable: GrantablePermissions;
}

function RoleOption({ role, grantable }: RoleOptionProps) {
  const missing = grantable.unsatisfiable(role.permissions);
  const escalated = missing.length > 0;
  const tooltip = escalated
    ? `This role has permissions you don't have (${missing.join(", ")}). Only a superadmin can assign it.`
    : undefined;

  return (
    <SelectItem
      value={String(role.id)}
      disabled={escalated}
      title={tooltip}
      className={escalated ? "cursor-not-allowed opacity-60" : undefined}
    >
      <span className="flex items-center gap-2">
        <span>{role.name}</span>
        {role.is_system && (
          <span className="text-[9px] uppercase tracking-wider text-slate-400">
            System
          </span>
        )}
        {escalated && (
          <span className="inline-flex items-center gap-1 rounded-full border border-amber-200 bg-amber-50 px-1.5 py-0 text-[9px] font-semibold uppercase tracking-wider text-amber-700">
            <Lock className="h-2.5 w-2.5" />
            Beyond your perms
          </span>
        )}
      </span>
    </SelectItem>
  );
}

// ─── Success cards ─────────────────────────────────────────────────────────

function InviteSuccessCard({
  response,
  onClose,
}: {
  response: InviteMemberResponse;
  onClose: () => void;
}) {
  return (
    <div className="space-y-4 pt-2">
      <div className="rounded-xl border border-emerald-200 bg-emerald-50/60 p-5">
        <div className="flex items-start gap-3">
          <CheckCircle2 className="mt-0.5 h-5 w-5 shrink-0 text-emerald-600" />
          <div className="min-w-0 flex-1">
            <p className="text-sm font-semibold text-emerald-900">
              Invitation sent to{" "}
              <span className="font-mono text-[13px]">{response.email}</span>
            </p>
            <p className="mt-1 text-xs leading-relaxed text-emerald-800/80">
              An email with a password-creation link is on the way. The link
              expires in 48 hours.
            </p>
          </div>
        </div>
      </div>

      <DialogFooter>
        <Button type="button" onClick={onClose}>
          Done
        </Button>
      </DialogFooter>
    </div>
  );
}

function AddSuccessCard({
  response,
  onClose,
}: {
  response: AddMemberResponse;
  onClose: () => void;
}) {
  const [copied, setCopied] = useState(false);

  async function copy() {
    if (!response.generated_password) return;
    try {
      await navigator.clipboard.writeText(
        `Email: ${response.email}\nPassword: ${response.generated_password}`,
      );
      setCopied(true);
      toast.success("Credentials copied to clipboard");
      setTimeout(() => setCopied(false), 2500);
    } catch {
      toast.error("Couldn't copy. Select manually instead.");
    }
  }

  return (
    <div className="space-y-4 pt-2">
      <div className="rounded-xl border border-emerald-200 bg-emerald-50/60 p-5">
        <div className="flex items-start gap-3">
          <CheckCircle2 className="mt-0.5 h-5 w-5 shrink-0 text-emerald-600" />
          <div className="min-w-0 flex-1">
            <p className="text-sm font-semibold text-emerald-900">
              Account created and added
            </p>
            <p className="mt-1 text-xs leading-relaxed text-emerald-800/80">
              Share these credentials securely. {response.full_name} should
              change their password on first sign-in.
            </p>
          </div>
        </div>
      </div>

      <div className="space-y-3 rounded-xl border border-amber-200 bg-amber-50/40 p-4">
        <dl className="space-y-2.5">
          <div className="flex items-baseline justify-between gap-3">
            <dt className="text-[10px] font-semibold uppercase tracking-wider text-amber-900">
              Email
            </dt>
            <dd className="truncate font-mono text-[13px] text-slate-900">
              {response.email}
            </dd>
          </div>
          <div className="flex items-baseline justify-between gap-3">
            <dt className="text-[10px] font-semibold uppercase tracking-wider text-amber-900">
              Password
            </dt>
            <dd className="truncate font-mono text-[13px] font-semibold text-slate-900">
              {response.generated_password ?? "—"}
            </dd>
          </div>
        </dl>

        <Button
          type="button"
          variant="outline"
          size="sm"
          onClick={copy}
          className="w-full border-amber-300 bg-white text-amber-900 hover:bg-amber-100"
        >
          {copied ? (
            <>
              <Check className="mr-2 h-3.5 w-3.5" />
              Copied
            </>
          ) : (
            <>
              <Copy className="mr-2 h-3.5 w-3.5" />
              Copy credentials
            </>
          )}
        </Button>

        <div className="flex items-start gap-2 rounded-lg border border-amber-200 bg-white/70 p-2.5 text-[11px] text-amber-900">
          <AlertTriangle className="mt-0.5 h-3.5 w-3.5 shrink-0" />
          <span>
            This password will not be shown again. Copy it before you close
            this dialog.
          </span>
        </div>
      </div>

      <DialogFooter>
        <Button type="button" onClick={onClose}>
          Done
        </Button>
      </DialogFooter>
    </div>
  );
}

// ─── Errors ────────────────────────────────────────────────────────────────

function extractInviteErrorMessage(err: unknown): string {
  if (axios.isAxiosError(err)) {
    if (err.response?.status === 409) {
      return "This user is already a member of the project.";
    }
    const detail = (err.response?.data as { detail?: unknown } | undefined)
      ?.detail;
    if (typeof detail === "string") return detail;
    if (Array.isArray(detail) && detail.length > 0) {
      const first = detail[0] as { msg?: string };
      if (first?.msg) return first.msg;
    }
    if (err.code === "ERR_NETWORK") {
      return "Cannot reach the server.";
    }
  }
  return "Couldn't add the member.";
}
